import React, { useState } from 'react';
import { saveStoryline, suggestLinks } from '../api/gameBuilderAPI';

/**
 * The plan's games, always linked (backend plan.chain_games: each game joins the end of the chain as it's built - no
 * storyline to create). Between each pair, a LINK: 🕐 time passes ("Two hours later") - the creator writes its words.
 * Where each game happens is its own background(s) - its views - so there's no "another room" link. Saved at once.
 */
const pairKey = (a, b) => `${a}>${b}`;

export default function ChainCard({ plan, onSaved, onPlay }) {
  const story = plan.storyline;
  const names = Object.fromEntries(plan.missions.map((m) => [m.id, m.name]));
  const built = (id) => !!plan.status?.[id]?.game;
  const [labels, setLabels] = useState({});          // link index -> what the creator is typing (saved on leaving it)
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const save = async (order, links) => {
    const out = await run('Saving…', () => saveStoryline(plan.id, order, links));
    if (out) { onSaved({ storyline: out.storyline }); setLabels({}); }
    return out;
  };

  // A new order keeps every link the creator set for the same pair; a new pair gets code's suggestion.
  const move = async (i, d) => {
    const order = [...story.missions];
    [order[i], order[i + d]] = [order[i + d], order[i]];
    const kept = Object.fromEntries(story.missions.slice(1).map((b, k) => [pairKey(story.missions[k], b), story.links[k]]));
    const fresh = await run('Suggesting links…', () => suggestLinks(plan.id, order));
    if (!fresh) return;
    save(order, order.slice(1).map((b, k) => kept[pairKey(order[k], b)] || fresh.links[k]));
  };

  const setLink = (i, patch) => save(story.missions, story.links.map((l, k) => (k === i ? { ...l, ...patch, type: 'time' } : l)));

  if (!story || story.missions.length === 0) {
    return (
      <section className="gb-card gb-story">
        <h2 className="gb-story-title">Simulation chain</h2>
        <p className="gb-muted">Paint a mission's scene and it starts the chain; each simulation after it joins the end.</p>
      </section>
    );
  }

  const playable = story.missions.filter(built);
  return (
    <section className="gb-card gb-story">
      <div className="gb-plan-head">
        <h2 className="gb-story-title">Simulation chain</h2>
        <button type="button" className="gb-btn gb-btn-sm" disabled={playable.length === 0 || !!busy} onClick={onPlay}>▶ Play the chain</button>
      </div>
      <ol className="gb-chain">
        {story.missions.map((id, i) => (
          <React.Fragment key={id}>
            <li className={`gb-chain-mission is-editing ${built(id) ? '' : 'is-unbuilt'}`}>
              <span className="gb-mid">{id}</span> <span className="gb-chain-name">{names[id]}</span>
              {!built(id) && <span className="gb-small gb-muted"> · being rebuilt - skipped until painted</span>}
              <span className="gb-chain-tools">
                <button type="button" className="gb-icon-btn" aria-label={`Move ${names[id]} earlier`} disabled={i === 0 || !!busy} onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="gb-icon-btn" aria-label={`Move ${names[id]} later`}
                  disabled={i === story.missions.length - 1 || !!busy} onClick={() => move(i, 1)}>↓</button>
              </span>
            </li>
            {i < story.links.length && (
              <li className="gb-chain-link is-editing">
                <span className="gb-small" aria-hidden="true">🕐</span>
                <input className="gb-link-label" maxLength={60} aria-label={`How much later ${names[story.missions[i + 1]]} begins`}
                  disabled={!!busy} value={labels[i] ?? story.links[i].label ?? ''} placeholder="Two hours later"
                  onChange={(e) => setLabels((x) => ({ ...x, [i]: e.target.value }))}
                  onBlur={() => labels[i] !== undefined && labels[i] !== story.links[i].label && setLink(i, { label: labels[i] })} />
              </li>
            )}
          </React.Fragment>
        ))}
      </ol>
      {busy && <p className="gb-progress"><span className="gb-spinner" /> {busy}</p>}
      {error && <div className="gb-error" role="alert">{error}</div>}
    </section>
  );
}
